import CloseAlertModal from 'features/Weather/features/WeatherAlerts/components/Modals/CloseAlertModal/CloseAlertModal';
import AlertDetails from 'features/Weather/features/WeatherAlerts/features/AlertList/features/AlertCard/components/AlertDetails/AlertDetails';
import AlertMap from 'features/Weather/features/WeatherAlerts/features/AlertList/features/AlertCard/components/AlertMap/AlertMap';
import { selectShowCloseAlertModal } from 'features/Weather/features/WeatherAlerts/features/AlertList/state/selectors/alertListSelectors';
import { setShowCloseAlertModal } from 'features/Weather/features/WeatherAlerts/features/AlertList/state/slices/alertListSlice';
import { closeAlertThunk } from 'features/Weather/features/WeatherAlerts/features/AlertList/state/thunks/alertListThunks';
import { isLegacyFemaAlert } from 'features/Weather/features/WeatherAlerts/features/FemaAlertList/utils/femaToAlert.adapter';
import {
  getAlertSeverityColor,
  getAlertSeverityLabel,
  getAlertSeverityTextColor,
} from 'features/Weather/features/WeatherAlerts/utils/alertSeverity.utils';
import useWindow from 'hooks/useWindow';
import { useState } from 'react';
import { Badge, Button } from 'react-bootstrap';
import { FaChevronDown, FaChevronLeft, FaChevronRight, FaChevronUp } from 'react-icons/fa6';
import { useDispatch, useSelector } from 'react-redux';
import { formatDateTimeRange } from 'utils/dates/date.utils';
import styles from './AlertCard.module.css';

/**
 * @param {{ alert: Alert, canCloseAlert?: boolean, canDeleteAlerts?: boolean, renderActions?: React.ReactNode, renderDetails?: React.ReactNode }} props
 */
const AlertCard = ({ alert, canCloseAlert, canDeleteAlerts, renderActions, renderDetails }) => {
  const dispatch = useDispatch();
  const showCloseAlertModal = useSelector(selectShowCloseAlertModal);
  const [showInfo, setShowInfo] = useState(false);
  const { width } = useWindow();
  const severityLabel = alert.severityLabel ?? getAlertSeverityLabel(alert.severity);
  const isFemaAlert = alert.alertSource === 'fema' || isLegacyFemaAlert(alert);

  return (
    <div className="d-flex flex-column flex-md-row rounded-4 mt-3 w-100 bg-primary">
      <div
        className={styles['alert-severity-tag']}
        style={{
          backgroundColor: getAlertSeverityColor(alert.severity),
          color: getAlertSeverityTextColor(alert.severity),
        }}
      >
        <dl className="m-0">
          <dt className="visually-hidden">Severity</dt>
          <dd className={`${styles['alert-severity-label']} m-0`}>{severityLabel}</dd>
        </dl>
      </div>
      <div className="min-w-0 flex-grow-1 py-3 px-3">
        <div className="w-100">
          <div
            onClick={() => setShowInfo(!showInfo)}
            className="d-flex flex-column flex-lg-row w-100 align-items-center cursor-pointer gap-3"
            style={{ borderBottom: showInfo && 'solid grey 1px' }}
          >
            {/* Card Info */}
            <div className="d-flex flex-column justify-content-center text-center text-lg-start flex-grow-1 min-w-0">
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="fs-5 fw-bold">{alert.description}</div>
                {isFemaAlert ? (
                  <Badge bg="warning" text="dark">
                    FEMA IPAWS
                  </Badge>
                ) : null}
                {alert.statusLabel ? (
                  <Badge bg={alert.statusBadgeVariant ?? 'secondary'}>{alert.statusLabel}</Badge>
                ) : null}
                {/* FEMA class badge. Only a FEMA card with a known class sets the label. */}
                {alert.environmentLabel ? (
                  <Badge
                    bg={alert.environmentBadgeVariant ?? 'secondary'}
                    data-testid="alert-environment-badge"
                  >
                    {alert.environmentLabel}
                  </Badge>
                ) : null}
                {alert.globalActionId != null ? (
                  <Badge bg="dark">
                    {alert.globalActionTitle
                      ? `Global Action: ${alert.globalActionTitle}`
                      : `Global Action ${alert.globalActionId}`}
                  </Badge>
                ) : null}
              </div>
              <div className="fs-5 mt-3">{alert.nws_office}</div>
              {alert.areaDescription ? <div className="fs-6">{alert.areaDescription}</div> : null}
              <div className="fs-5">
                In Effect{' '}
                {formatDateTimeRange(alert.issue_time_utc, alert.valid_until_time_utc, '2-digit')}
              </div>
              <div className="fs-5">
                {alert.processedCenter
                  ? `Coordinates: ${Number(alert.processedCenter[0]).toFixed(5)}° N, ${Number(alert.processedCenter[1]).toFixed(5)}° W`
                  : 'No Coordinates Provided'}
              </div>
            </div>
            {/* Map */}
            <AlertMap alert={alert} />
            {/* Buttons */}
            <div
              className="d-flex flex-column flex-lg-row position-relative flex-shrink-0"
              style={{ width: width < 992 && '100%', minWidth: width >= 992 ? 56 : undefined }}
            >
              {renderActions}
              {canCloseAlert && (
                <Button
                  className="rounded-4 p-2 position-absolute"
                  style={{
                    width: width >= 992 ? '80px' : '100%',
                    right: width >= 992 ? 56 : 0,
                    top: width >= 992 ? '50%' : -52,
                    transform: width >= 992 ? 'translateY(-50%)' : undefined,
                    zIndex: 2,
                    backgroundColor: 'var(--color-success-button)',
                    color: 'var(--color-primary-highlight-foreground)',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(setShowCloseAlertModal(true));
                  }}
                  variant="success"
                >
                  Close
                </Button>
              )}
              {width >= 992 ? (
                <Button onClick={() => setShowInfo(!showInfo)} variant="secondary">
                  {showInfo ? <FaChevronLeft size={40} /> : <FaChevronRight size={40} />}
                </Button>
              ) : (
                <Button onClick={() => setShowInfo(!showInfo)} variant="secondary">
                  {showInfo ? <FaChevronUp size={30} /> : <FaChevronDown size={30} />}
                </Button>
              )}
            </div>
          </div>
        </div>
        {showInfo && (renderDetails ?? <AlertDetails alert={alert} />)}
      </div>
      <CloseAlertModal
        show={showCloseAlertModal}
        alert={alert}
        onClose={() => dispatch(setShowCloseAlertModal(false))}
        onConfirm={(notes) => {
          if (!canDeleteAlerts) return;
          dispatch(closeAlertThunk({ alertId: alert.message_id, notes: notes }));
        }}
      />
    </div>
  );
};

export default AlertCard;
